import { useEffect, useState } from "react";
import {
  TrendUp, TrendDown, Wallet, ArrowsLeftRight, ArrowDownLeft, ArrowUpRight, Bank, Warning,
} from "@phosphor-icons/react";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import api from "@/lib/api";
import { toast } from "sonner";
import { useCurrency } from "@/context/CurrencyContext";
import { formatMoney } from "@/lib/format";

const CHART_COLORS = ["#D4AF37", "#10B981", "#3B82F6", "#EF4444", "#8B5CF6", "#F59E0B", "#EC4899"];

function StatCard({ label, value, icon: Icon, accent, sub, testid }) {
  return (
    <div data-testid={testid} className="bg-card border border-border rounded-lg p-6 transition-transform hover:-translate-y-1">
      <div className="flex items-start justify-between">
        <span className="text-xs tracking-[0.1em] uppercase text-muted-foreground">{label}</span>
        <Icon size={22} weight="regular" style={{ color: accent }} />
      </div>
      <div className="mt-4 font-heading text-2xl font-semibold font-mono-num" style={{ color: accent || undefined }}>
        {value}
      </div>
      {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const { display, currencies } = useCurrency();
  const [data, setData] = useState(null);
  const [overBudgets, setOverBudgets] = useState([]);
  const [upcoming, setUpcoming] = useState(null);

  useEffect(() => {
    api.get(`/dashboard?display=${display}`).then((res) => setData(res.data)).catch(() => {});
    api.get(`/dashboard/upcoming-by-account?display=${display}`).then((res) => setUpcoming(res.data)).catch(() => {});
  }, [display]);

  useEffect(() => {
    api.get("/budgets").then((res) => setOverBudgets(res.data.filter((b) => b.over))).catch(() => {});
  }, []);

  const fm = (v) => formatMoney(v, display, currencies);

  const emailRisk = () => {
    toast.promise(api.post("/reports/cash-risk-email"), {
      loading: "E-posta gönderiliyor...",
      success: (r) => r.data.status === "sent" ? "Nakit uyarısı e-postası gönderildi" : "Riskli hesap yok",
      error: "E-posta gönderilemedi",
    });
  };

  if (!data)
    return (
      <div className="flex items-center justify-center h-64">
        <div className="h-6 w-6 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    );

  return (
    <div className="space-y-8 animate-fade-in-up">
      <div>
        <h1 className="font-heading text-3xl font-light tracking-tight">Panel</h1>
        <p className="text-sm text-muted-foreground mt-1">Nakit akışınızı ve finansal durumunuzu anlık takip edin</p>
      </div>

      {overBudgets.length > 0 && (
        <div data-testid="dashboard-budget-alert" className="flex items-center gap-3 border border-destructive/40 bg-destructive/10 rounded-lg p-4">
          <Warning size={22} className="text-destructive" weight="fill" />
          <span className="text-sm text-destructive">
            Bütçe aşımı: {overBudgets.map((b) => b.category).join(", ")} kategorisinde limit aşıldı.
          </span>
        </div>
      )}

      {upcoming && upcoming.risk_accounts && upcoming.risk_accounts.length > 0 && (
        <div data-testid="cash-risk-alert" className="border border-destructive/40 bg-destructive/10 rounded-lg p-4">
          <div className="flex items-center gap-3 mb-2">
            <Warning size={22} className="text-destructive" weight="fill" />
            <span className="text-sm font-medium text-destructive">Nakit Uyarısı — Önümüzdeki 30 günde bazı hesaplarda bakiye yetersiz kalabilir</span>
            <button onClick={emailRisk} data-testid="cash-risk-email-btn"
              className="ml-auto text-xs px-3 py-1 rounded-md border border-destructive/40 text-destructive hover:bg-destructive/10 transition-colors">
              E-posta gönder
            </button>
          </div>
          <div className="space-y-1 pl-9">
            {upcoming.risk_accounts.map((r) => (
              <div key={r.account_id} data-testid={`cash-risk-${r.account_id}`} className="text-sm text-destructive/90">
                <span className="font-medium">{r.name}</span>: bakiye {fm(r.balance)}, 30 günlük net çıkış sonrası <span className="font-semibold">{fm(r.projected)}</span> ({fm(r.shortfall)} açık)
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testid="stat-balance" label="Toplam Bakiye" value={fm(data.total_balance)} icon={Wallet} accent="#D4AF37"
          sub={`${data.account_count} hesap`} />
        <StatCard testid="stat-income" label="Toplam Gelir" value={fm(data.total_income)} icon={TrendUp} accent="#10B981" />
        <StatCard testid="stat-expense" label="Toplam Gider" value={fm(data.total_expense)} icon={TrendDown} accent="#EF4444" />
        <StatCard testid="stat-net" label="Net Nakit Akışı" value={fm(data.net_cashflow)} icon={ArrowsLeftRight}
          accent={data.net_cashflow >= 0 ? "#10B981" : "#EF4444"} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Cashflow chart */}
        <div className="lg:col-span-2 bg-card border border-border rounded-lg p-6">
          <h2 className="font-heading text-lg mb-6">Aylık Nakit Akışı (Son 6 Ay)</h2>
          <div className="h-72" data-testid="cashflow-chart">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.monthly}>
                <defs>
                  <linearGradient id="gInc" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10B981" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gExp" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#EF4444" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#EF4444" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272A" vertical={false} />
                <XAxis dataKey="month" stroke="#A1A1AA" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke="#A1A1AA" fontSize={12} tickLine={false} axisLine={false}
                  tickFormatter={(v) => new Intl.NumberFormat("tr-TR", { notation: "compact" }).format(v)} />
                <Tooltip
                  contentStyle={{ background: "#18181B", border: "1px solid #27272A", borderRadius: 8, fontSize: 13 }}
                  formatter={(v, n) => [fm(v), n === "income" ? "Gelir" : "Gider"]} />
                <Area type="monotone" dataKey="income" stroke="#10B981" strokeWidth={2} fill="url(#gInc)" />
                <Area type="monotone" dataKey="expense" stroke="#EF4444" strokeWidth={2} fill="url(#gExp)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Category pie */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h2 className="font-heading text-lg mb-6">Gider Dağılımı</h2>
          {data.categories.length === 0 ? (
            <p className="text-sm text-muted-foreground">Henüz gider kaydı yok.</p>
          ) : (
            <div className="h-72" data-testid="category-chart">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={data.categories} dataKey="value" nameKey="name" cx="50%" cy="45%" outerRadius={80} innerRadius={45} paddingAngle={2}>
                    {data.categories.map((_, i) => (
                      <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} stroke="#09090B" />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ background: "#18181B", border: "1px solid #27272A", borderRadius: 8, fontSize: 13 }} formatter={(v) => fm(v)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      {/* Upcoming + Accounts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-card border border-border rounded-lg p-6 flex items-center gap-5">
          <div className="h-12 w-12 rounded-md bg-success/10 flex items-center justify-center">
            <ArrowDownLeft size={24} className="text-[#10B981]" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground">Yaklaşan Tahsilatlar</div>
            <div className="font-heading text-xl font-semibold font-mono-num text-[#10B981]">{fm(data.upcoming_receivable)}</div>
          </div>
        </div>
        <div className="bg-card border border-border rounded-lg p-6 flex items-center gap-5">
          <div className="h-12 w-12 rounded-md bg-destructive/10 flex items-center justify-center">
            <ArrowUpRight size={24} className="text-[#EF4444]" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground">Yaklaşan Ödemeler</div>
            <div className="font-heading text-xl font-semibold font-mono-num text-[#EF4444]">{fm(data.upcoming_payable)}</div>
          </div>
        </div>
        <div className="bg-card border border-border rounded-lg p-6 flex items-center gap-5">
          <div className="h-12 w-12 rounded-md bg-primary/10 flex items-center justify-center">
            <Bank size={24} className="text-primary" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground">Aktif Hesaplar</div>
            <div className="font-heading text-xl font-semibold font-mono-num">{data.account_count}</div>
          </div>
        </div>
      </div>

      {/* Next 30 days outflow by account */}
      {upcoming && (
        <div className="bg-card border border-border rounded-lg p-6" data-testid="upcoming-by-account">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
            <h2 className="font-heading text-lg">Önümüzdeki 30 Gün — Hesap Bazlı Nakit Çıkışı</h2>
            <div className="flex items-center gap-4 text-sm">
              <span className="text-muted-foreground">Toplam Çıkış: <span className="font-mono-num font-semibold text-[#EF4444]">{fm(upcoming.total_out)}</span></span>
              <span className="text-muted-foreground">Toplam Giriş: <span className="font-mono-num font-semibold text-[#10B981]">{fm(upcoming.total_in)}</span></span>
            </div>
          </div>
          {upcoming.accounts.length === 0 ? (
            <p className="text-sm text-muted-foreground">Önümüzdeki 30 günde bekleyen ödeme/tahsilat yok.</p>
          ) : (
            <div className="space-y-2">
              {upcoming.accounts.map((a) => (
                <div key={a.account_id || "none"} data-testid={`upcoming-acc-${a.account_id || "none"}`}
                  className="flex items-center gap-4 border border-border rounded-md p-3 bg-secondary/20">
                  <div className="h-9 w-9 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
                    <Bank size={18} className="text-primary" />
                  </div>
                  <div className="flex-1 min-w-0 text-sm font-medium truncate">{a.name}</div>
                  {a.in > 0 && (
                    <div className="text-right">
                      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Giriş</div>
                      <div className="font-mono-num font-semibold text-sm text-[#10B981]">+{fm(a.in)}</div>
                    </div>
                  )}
                  <div className="text-right min-w-[110px]">
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Çıkış</div>
                    <div className="font-mono-num font-semibold text-sm text-[#EF4444]">{a.out > 0 ? `−${fm(a.out)}` : fm(0)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {data.top_debtors && data.top_debtors.length > 0 && (
        <div className="bg-card border border-border rounded-lg p-6" data-testid="top-debtors">
          <h2 className="font-heading text-lg mb-4">En Çok Borçlu Cariler</h2>
          <div className="space-y-2">
            {data.top_debtors.map((c, i) => (
              <div key={c.id} data-testid={`debtor-${c.id}`} className="flex items-center gap-4 border border-border rounded-md p-3 bg-secondary/20">
                <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center text-xs font-semibold text-primary shrink-0">{i + 1}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{c.name}</div>
                  <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{c.type === "customer" ? "Müşteri" : "Tedarikçi"}</div>
                </div>
                <div className="font-mono-num font-semibold text-sm text-[#EF4444]">{formatMoney(c.remaining, c.currency, currencies)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Accounts list */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h2 className="font-heading text-lg mb-4">Banka Hesapları</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {data.accounts.map((a) => (
            <div key={a.id} className="border border-border rounded-md p-4 bg-secondary/30">
              <div className="text-sm font-medium truncate">{a.name}</div>
              <div className="text-xs text-muted-foreground truncate">{a.bank_name}</div>
              <div className="mt-3 font-mono-num text-lg font-semibold">{formatMoney(a.balance, a.currency, currencies)}</div>
              <div className="text-[10px] uppercase tracking-wide text-primary mt-1">{a.currency}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
